import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useNotificationActions } from "~/shared/hooks/use-notifications.ts";
import type { PostRoutingGuideType } from "~/shared/schemas/routing-guide-schema";
import { postRoutingGuideSchema } from "~/shared/schemas/routing-guide-schema.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { postAndValidateTraxAr } from "~/shared/services/api/fetch-client.ts";

const postNewRoutingGuide = async (newGuide: PostRoutingGuideType) => {
	const url = apiRoutes.POSTGLOBALROUTINGGUIDE;
	const response = await postAndValidateTraxAr(
		url,
		newGuide,
		postRoutingGuideSchema,
	);
	if (!response.success) {
		throw new Error(
			`Failed to create routing guide: ${response.error}`,
			response.details instanceof Error
				? { cause: response.details }
				: undefined,
		);
	}
	return response.data;
};

export const useCreateRoutingGuide = () => {
	const queryClient = useQueryClient();
	const { showError, showSuccess } = useNotificationActions();

	const { mutate: createRoutingGuide } = useMutation({
		mutationFn: (newGuide: PostRoutingGuideType) =>
			postNewRoutingGuide(newGuide),

		onSuccess: () => {
			showSuccess("Routing Guide Created");
			void queryClient.invalidateQueries({ queryKey: ["globalRoutingGuides"] });
			void queryClient.invalidateQueries({
				queryKey: ["customerRoutingGuides"],
			});
		},

		onError: (_err) => {
			showError(`${_err}`);
		},
	});

	return { createRoutingGuide };
};
